import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/** A value that only settles once the user stops typing. */
export function useDebounced<T>(value: T, delay = 220): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

/**
 * Scroll the document while a task is being dragged near either viewport edge.
 *
 * Native HTML drag events do not keep firing on the source element once the
 * pointer has travelled over another panel, so this intentionally listens at
 * document level. The approach zone stays deliberately tight: people need to
 * be able to place an item in the middle of a long calendar without the page
 * beginning to move underneath it.
 */
export function taskDragAutoScrollVelocity({
  clientY,
  top,
  height,
}: {
  clientY: number;
  top: number;
  height: number;
}) {
  if (height <= 0) return 0;

  // A calendar can be tall, but its autoscroll target must remain at the real
  // top/bottom edge. At desktop heights this is roughly 64–88px, rather than
  // the former third of the viewport.
  const edge = Math.min(88, Math.max(56, height * 0.09));
  const bottom = top + height;
  const towardTop = Math.max(0, (top + edge - clientY) / edge);
  const towardBottom = Math.max(0, (clientY - (bottom - edge)) / edge);
  const direction = towardBottom - towardTop;
  const curve = Math.min(1, Math.abs(direction));

  return direction === 0 ? 0 : Math.round(Math.sign(direction) * (4 + 16 * curve ** 2));
}

export function useTaskDragAutoScroll(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    let frame: number | null = null;
    let speed = 0;
    const scrollSurface =
      document.querySelector<HTMLElement>("main.cx-main-canvas") ??
      document.querySelector<HTMLElement>("main") ??
      document.documentElement;

    const stop = () => {
      speed = 0;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      scrollSurface.classList.remove("cx-drag-scrolling");
    };

    const tick = () => {
      if (speed === 0) {
        frame = null;
        return;
      }

      const atTop = scrollSurface.scrollTop <= 0;
      const atBottom =
        scrollSurface.scrollTop + scrollSurface.clientHeight >= scrollSurface.scrollHeight - 1;
      if ((speed < 0 && atTop) || (speed > 0 && atBottom)) {
        stop();
        return;
      }
      scrollSurface.scrollBy({ top: speed, behavior: "auto" });
      frame = requestAnimationFrame(tick);
    };

    const onDragOver = (event: DragEvent) => {
      const types = Array.from(event.dataTransfer?.types ?? []);
      if (
        !types.includes("application/x-exognitos-task") &&
        !types.includes("application/x-exognitos-schedule-block")
      ) {
        stop();
        return;
      }

      const bounds = scrollSurface.getBoundingClientRect();
      speed = taskDragAutoScrollVelocity({
        clientY: event.clientY,
        top: bounds.top,
        height: bounds.height,
      });
      if (speed === 0) {
        stop();
        return;
      }

      scrollSurface.classList.add("cx-drag-scrolling");
      if (frame === null) frame = requestAnimationFrame(tick);
    };

    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragend", stop);
    document.addEventListener("drop", stop);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragend", stop);
      document.removeEventListener("drop", stop);
      stop();
    };
  }, [enabled]);
}

/** A one-second tick, for a running timer only. */
export function useSecondTick(active: boolean): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return tick;
}

/** Media query as state, for layout decisions React has to make. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);
  return matches;
}

export const BREAKPOINTS = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)",
} as const;

export function useBreakpoint(name: keyof typeof BREAKPOINTS): boolean {
  return useMediaQuery(BREAKPOINTS[name]);
}

/** Persisted local UI state — pane widths, collapsed groups, last view. */
export function useLocalState<T>(
  key: string,
  initial: T,
  validate?: (value: unknown) => value is T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      const parsed: unknown = stored === null ? initial : JSON.parse(stored);
      return validate && !validate(parsed) ? initial : (parsed as T);
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (next: React.SetStateAction<T>) => {
      setValue((current) => {
        const resolved = typeof next === "function" ? (next as (value: T) => T)(current) : next;
        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // A full or disabled storage is not worth failing an interaction over.
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, set];
}

/** Fire when a click lands outside the referenced element. */
export function useClickOutside<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  handler: () => void,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return;
    const listener = (event: MouseEvent | TouchEvent) => {
      const element = ref.current;
      if (!element || element.contains(event.target as Node)) return;
      handler();
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler, enabled]);
}

type EscapeLayer = {
  id: symbol;
  close: () => void;
  element?: React.RefObject<HTMLElement | null>;
  depth: number;
};
const escapeLayers: EscapeLayer[] = [];

/**
 * How deeply nested a dismissable layer is.
 *
 * Every modal layer renders through a portal, so a dialog opened *from inside*
 * another dialog becomes its DOM sibling and document order stops describing
 * which one is on top — React may even insert the nested one first. Nesting is
 * still exact in the React tree, so each layer publishes its own depth to
 * whatever it renders, and Escape closes the deepest layer rather than the last
 * element in the body.
 */
const EscapeDepthContext = createContext(0);

/** Marks everything rendered inside one dismissable layer as one level deeper. */
export function EscapeLayerBoundary({ children }: { children: ReactNode }) {
  const depth = useContext(EscapeDepthContext);
  return createElement(EscapeDepthContext.Provider, { value: depth + 1 }, children);
}

function laterEscapeLayer(current: EscapeLayer, candidate: EscapeLayer): EscapeLayer {
  if (candidate.depth !== current.depth) {
    return candidate.depth > current.depth ? candidate : current;
  }
  const currentElement = current.element?.current;
  const candidateElement = candidate.element?.current;
  if (!currentElement || !candidateElement) return candidate;
  if (currentElement.contains(candidateElement)) return candidate;
  if (candidateElement.contains(currentElement)) return current;
  const position = currentElement.compareDocumentPosition(candidateElement);
  return position & Node.DOCUMENT_POSITION_FOLLOWING ? candidate : current;
}

function onEscapeKeyDown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  const connectedLayers = escapeLayers.filter(
    (layer) => !layer.element?.current || layer.element.current.isConnected,
  );
  const top = connectedLayers.reduce<EscapeLayer | undefined>(
    (current, candidate) => (current ? laterEscapeLayer(current, candidate) : candidate),
    undefined,
  );
  if (!top) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  top.close();
}

function registerEscapeLayer(layer: EscapeLayer) {
  if (escapeLayers.length === 0) document.addEventListener("keydown", onEscapeKeyDown, true);
  escapeLayers.push(layer);
  return () => {
    const index = escapeLayers.findIndex((candidate) => candidate.id === layer.id);
    if (index >= 0) escapeLayers.splice(index, 1);
    if (escapeLayers.length === 0) document.removeEventListener("keydown", onEscapeKeyDown, true);
  };
}

/** Escape closes exactly the topmost registered layer. */
export function useEscape(
  handler: () => void,
  enabled = true,
  element?: React.RefObject<HTMLElement | null>,
  layerDepth?: number,
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const inheritedDepth = useContext(EscapeDepthContext);
  const depth = Math.max(inheritedDepth, layerDepth ?? 0);

  useEffect(() => {
    if (!enabled) return;
    const layer: EscapeLayer = {
      id: Symbol("escape-layer"),
      close: () => handlerRef.current(),
      element,
      depth,
    };
    return registerEscapeLayer(layer);
  }, [depth, element, enabled]);
}

/**
 * Keep keyboard focus inside an open dialog.
 *
 * Without this a screen-reader user tabs straight out of a modal and into the
 * page behind it, which is disorienting in a way that is invisible to anyone
 * using a mouse.
 */
export function useFocusTrap<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  active: boolean,
) {
  const retainedFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!active) return;
    const element = ref.current;
    if (!element) return;

    const previous = document.activeElement as HTMLElement | null;
    const selector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const focusFirst = () => {
      // A pointer or autoFocus may already have chosen a field during the
      // opening frame. Do not replace that choice with the header control.
      if (document.activeElement !== previous && element.contains(document.activeElement)) return;
      const focusable = element.querySelectorAll<HTMLElement>(selector);
      // React's `autoFocus` runs before this animation-frame trap. Prefer the
      // explicitly requested target so the trap does not immediately steal
      // focus back to an earlier close button in the dialog header.
      const preferred = element.querySelector<HTMLElement>("[autofocus]");
      const retained = retainedFocus.current;
      (retained && element.contains(retained)
        ? retained
        : (preferred ?? focusable[0] ?? element)
      ).focus();
    };
    const frame = requestAnimationFrame(focusFirst);

    // `offsetParent` looks like the obvious visibility test and is wrong here:
    // it is null for any `position: fixed` element, which a dialog panel and
    // everything inside it may well be. Computed style answers the question
    // that was actually being asked.
    const isVisible = (node: HTMLElement) => {
      const style = getComputedStyle(node);
      return (
        !node.closest('[hidden], [inert], [aria-hidden="true"]') &&
        style.display !== "none" &&
        style.visibility !== "hidden"
      );
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = [...element.querySelectorAll<HTMLElement>(selector)].filter(isVisible);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    element.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener("keydown", onKeyDown);
      if (element.contains(document.activeElement))
        retainedFocus.current = document.activeElement as HTMLElement;
      if (previous?.isConnected && isVisible(previous)) previous.focus();
      else if (element.contains(document.activeElement))
        (document.activeElement as HTMLElement)?.blur();
    };
  }, [ref, active]);
}

let scrollLockCount = 0;
let scrollLockOriginal = "";

/** Stop the page behind one or more modal layers from scrolling under them. */
export function useScrollLock(active: boolean) {
  useLayoutEffect(() => {
    if (!active) return;
    if (scrollLockCount === 0) {
      scrollLockOriginal = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    scrollLockCount += 1;
    return () => {
      scrollLockCount = Math.max(0, scrollLockCount - 1);
      if (scrollLockCount === 0) {
        document.body.style.overflow = scrollLockOriginal;
        scrollLockOriginal = "";
      }
    };
  }, [active]);
}

/** A stable id for label/aria wiring. */
export function useId(prefix: string): string {
  const ref = useRef<string>(undefined);
  if (!ref.current) {
    ref.current = `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
  }
  return ref.current;
}
